// =============================================================================
// io/sync/ingestKeys.ts — mint, list and revoke a realtime-sync key.
// -----------------------------------------------------------------------------
// ── 🚨 1. THE HASH MUST MATCH THE SERVER BYTE FOR BYTE, OR THE KEY SILENTLY DIES ──
//
// `supabase/functions/trade-ingest` looks a key up by `sha256Hex(presentedKey)`
// and takes the row where it equals `key_hash`. There is no fuzzy match and no
// second attempt. Get the encoding wrong here — miss the zero-pad, use upper
// case, hash the wrong string — and the key mints cleanly, pastes cleanly, and
// then every trade the logger pushes comes back 401 with nothing on any screen
// to explain it.
//
// ⚠ That failure is invisible from this side, which is why the hex encoder is
// pinned to a published SHA-256 vector in the tests rather than to its own
// output.
//
// ── 🚨 2. THE PLAINTEXT IS RETURNED ONCE AND NEVER STORED ──
//
// It is generated in the browser, hashed, and the HASH is what goes to the
// database. The plaintext is handed back to the caller and never written
// anywhere else — not to a table, not to a log, not into a second variable that
// outlives the modal showing it.
//
// ── 🚨 3. REVOKE VERIFIES THAT A ROW ACTUALLY CHANGED ──
//
// The old client fires the update and reports success on a clean response. But
// PostgREST returns a clean, empty result when the filter matched nothing — so a
// revoke against a key that RLS hides, or that another tab already handled,
// reports "Key revoked" and leaves it live.
//
// For a leaked bearer secret that is the worst possible lie: the user believes
// the key is dead, stops worrying, and it keeps accepting trades. This checks the
// returned rows and says so when nothing changed.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { KEY_PREFIX, keyPrefixOf, type IngestKey } from '../../domain/ingestKeys';
import { reportFailureAs } from '../diagnostics/errorLog';

/**
 * The columns the client may read.
 *
 * ⚠ `key_hash` IS ABSENT AND MUST STAY ABSENT. RLS on this table is row-scoped,
 * not column-scoped, so `select('*')` would hand the hash to the browser. It has
 * no use here — only the Edge Function, holding the service role, compares
 * against it.
 */
export const INGEST_KEY_COLUMNS =
  'id, key_prefix, label, account_id, source, created_at, last_used_at';

export function ingestKeyFromDb(r: Record<string, unknown>): IngestKey {
  return {
    id: String(r['id'] ?? ''),
    keyPrefix: String(r['key_prefix'] ?? ''),
    label: String(r['label'] ?? ''),
    accountId: (r['account_id'] as string | null) ?? null,
    source: String(r['source'] ?? 'atas'),
    createdAt: (r['created_at'] as string | null) ?? null,
    lastUsedAt: (r['last_used_at'] as string | null) ?? null,
  };
}

/**
 * The live keys, newest first.
 *
 * ⚠ `.is('revoked_at', null)` — revocation is a SOFT delete, and the row stays
 * so the Edge Function can keep rejecting the key by hash. Dropping this filter
 * would list dead keys as though they still worked.
 */
export async function loadIngestKeys(sb: SupabaseClient): Promise<IngestKey[]> {
  const { data, error } = await sb
    .from('ingest_keys')
    .select(INGEST_KEY_COLUMNS)
    .is('revoked_at', null)
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Couldn’t read your keys: ${error.message}`);
  return (data ?? []).map((r) => ingestKeyFromDb(r as unknown as Record<string, unknown>));
}

// ---------------------------------------------------------------------------
// Minting
// ---------------------------------------------------------------------------

/** 24 random bytes → 32 base64url characters. The old client's size. */
export const KEY_BYTES = 24;

const webRandom = (n: number): Uint8Array => crypto.getRandomValues(new Uint8Array(n));

/**
 * A fresh key.
 *
 * ⚠ `getRandomValues`, NEVER `Math.random`. This is a bearer secret; a
 * predictable one is not a secret at all, and the difference is invisible in
 * every test that only checks the shape.
 *
 * ⚠ base64**url** — `+` and `/` are replaced and the padding stripped, so the
 * key survives being pasted into an indicator settings box, a URL, or a config
 * file without escaping.
 *
 * @param randomBytes injectable so a test can pin the output. The default is the
 *   only one production ever uses.
 */
export function mintIngestKey(randomBytes: (n: number) => Uint8Array = webRandom): string {
  const bytes = randomBytes(KEY_BYTES);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  const b64 = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return KEY_PREFIX + b64;
}

/**
 * SHA-256, lowercase hex.
 *
 * ⚠ `padStart(2, '0')` IS LOAD-BEARING. Without it every byte below 0x10
 * contributes one character instead of two, and roughly one key in sixteen gets
 * a hash the server can never match — an intermittent failure that would look
 * like a flaky network for weeks. Identical to the Edge Function's own encoder,
 * deliberately.
 */
export async function sha256Hex(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface NewKeyRequest {
  label: string;
  accountId: string | null;
  source: string;
}

export interface CreateKeyResult {
  /** ⚠ Shown ONCE. The caller must not persist it. `null` on failure. */
  plaintext: string | null;
  error: string | null;
}

export async function createIngestKey(
  sb: SupabaseClient, userId: string, req: NewKeyRequest,
  randomBytes?: (n: number) => Uint8Array,
): Promise<CreateKeyResult> {
  if (!userId) return { plaintext: null, error: 'Not signed in.' };

  const plaintext = mintIngestKey(randomBytes);
  const { error } = await sb.from('ingest_keys').insert({
    user_id: userId,
    key_hash: await sha256Hex(plaintext),
    key_prefix: keyPrefixOf(plaintext),
    label: req.label.trim(),
    source: req.source || 'atas',
    // ⚠ Empty string → NULL, not ''. `account_id` is a foreign key; an empty
    // string is not a missing value and would fail the constraint.
    account_id: req.accountId || null,
  });

  if (error) {
    return {
      plaintext: null,
      error: reportFailureAs('create ingest key', error, `Could not create the key: ${error.message}`),
    };
  }
  return { plaintext, error: null };
}

/**
 * Revoke — see note 3 above.
 *
 * @param now an ISO timestamp, injected. `revoked_at` is what the Edge Function
 *   checks, so the value has to be the caller's, not a second clock's.
 */
export async function revokeIngestKey(
  sb: SupabaseClient, userId: string, id: string, now: string,
): Promise<{ error: string | null }> {
  const { data, error } = await sb
    .from('ingest_keys')
    .update({ revoked_at: now })
    .eq('user_id', userId)
    .eq('id', id)
    .select('id');

  if (error) {
    return {
      error: reportFailureAs('revoke ingest key', error, `Revoke failed: ${error.message}`),
    };
  }
  if (!Array.isArray(data) || data.length === 0) {
    // ⚠ Says the key MAY STILL BE LIVE. "Nothing happened" is not reassuring
    // here — the user came to this button because they think the key leaked.
    return {
      error: 'That revoke changed nothing, so the key may still be live. Reload the '
        + 'list and try again — if it is gone from the list, another tab revoked it.',
    };
  }
  return { error: null };
}
